import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import type { FileRecord, Membership, Tenant } from '../domain-types';
import { organisationDisplayName } from '../utils/organisation-display';
import { LocationDirectoryPanel, TeamDirectoryPanel } from './organisation-directory-panels';

export function OrganisationPage({
  tenant,
  editable,
  canManageLogo = editable,
  busy,
  personnelPanel,
  activityPanel,
  managerChoices = [],
  logoChoices = [],
  apiUrl,
  accessToken,
  platformSupportReason,
  onDirectoryChanged,
  onUploadLogo,
  onSaveProfile,
}: {
  tenant: Tenant | null;
  editable: boolean;
  canManageLogo?: boolean;
  busy: boolean;
  personnelPanel?: ReactNode;
  activityPanel?: ReactNode;
  managerChoices?: Membership[];
  logoChoices?: FileRecord[];
  apiUrl?: string;
  accessToken?: string;
  platformSupportReason?: string | undefined;
  onDirectoryChanged?: () => Promise<void>;
  onUploadLogo?: (file: File) => Promise<FileRecord | undefined>;
  onSaveProfile: (
    profile: Omit<
      Pick<
        Tenant,
        | 'legalName'
        | 'tradingName'
        | 'description'
        | 'contactEmail'
        | 'phone'
        | 'website'
        | 'brandColour'
        | 'logoFileId'
        | 'address'
      >,
      'logoFileId'
    > & { logoFileId?: string | null },
  ) => Promise<void>;
}) {
  const [activeTab, setActiveTab] = useState<
    'overview' | 'profile' | 'locations' | 'teams' | 'personnel' | 'activity'
  >('overview');
  const [legalName, setLegalName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [description, setDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [brandColour, setBrandColour] = useState('#163a31');
  const [logoFileId, setLogoFileId] = useState('');
  const [addressLines, setAddressLines] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  const [country, setCountry] = useState('Australia');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const tabListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLegalName(tenant?.legalName ?? tenant?.name ?? '');
    setTradingName(tenant?.tradingName ?? '');
    setDescription(tenant?.description ?? '');
    setContactEmail(tenant?.contactEmail ?? '');
    setPhone(tenant?.phone ?? '');
    setWebsite(tenant?.website ?? '');
    setBrandColour(tenant?.brandColour ?? '#163a31');
    setLogoFileId(tenant?.logoFileId ?? '');
    setAddressLines((tenant?.address?.addressLines ?? []).join('\n'));
    setSuburb(tenant?.address?.suburb ?? '');
    setState(tenant?.address?.state ?? '');
    setPostcode(tenant?.address?.postcode ?? '');
    setCountry(tenant?.address?.country ?? 'Australia');
  }, [tenant]);

  const handleTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    const tabs = [
      ...(tabListRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? []),
    ];
    if (tabs.length === 0) return;

    const currentIndex = tabs.findIndex((tab) => tab === document.activeElement);
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabs.length - 1
          : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const nextTab = tabs[nextIndex];
    const nextValue = nextTab?.dataset.tab as
      'overview' | 'profile' | 'locations' | 'teams' | 'personnel' | 'activity' | undefined;
    if (!nextTab || !nextValue) return;
    setActiveTab(nextValue);
    nextTab.focus();
  };

  if (!tenant) {
    return (
      <section className="organisation-page" aria-labelledby="organisation-page-title">
        <div className="workspace-heading">
          <div>
            <p className="eyebrow">Organisation</p>
            <h2 id="organisation-page-title">Organisation</h2>
          </div>
          <span>View only</span>
        </div>
        <p className="empty-state">No organisation is currently selected.</p>
      </section>
    );
  }

  const saveProfile = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const physicalAddressLines = addressLines
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    void onSaveProfile({
      ...(legalName.trim() ? { legalName: legalName.trim() } : {}),
      ...(tradingName.trim() ? { tradingName: tradingName.trim() } : {}),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(contactEmail.trim() ? { contactEmail: contactEmail.trim() } : {}),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(website.trim() ? { website: website.trim() } : {}),
      ...(brandColour ? { brandColour } : {}),
      logoFileId: logoFileId || null,
      ...(physicalAddressLines.length > 0 && suburb.trim() && state.trim() && postcode.trim()
        ? {
            address: {
              addressLines: physicalAddressLines,
              suburb: suburb.trim(),
              state: state.trim(),
              postcode: postcode.trim(),
              country: country.trim() || 'Australia',
            },
          }
        : {}),
    });
  };

  const uploadLogo = async (file: File | undefined) => {
    if (!file || !onUploadLogo) return;
    setUploadingLogo(true);
    try {
      const uploaded = await onUploadLogo(file);
      if (uploaded) setLogoFileId(uploaded.id);
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="organisation-page-layout">
      <section className="organisation-page" aria-labelledby="organisation-page-title">
        <div className="workspace-heading">
          <div>
            <p className="eyebrow">Organisation</p>
            <h2 id="organisation-page-title">{organisationDisplayName(tenant.name)}</h2>
          </div>
          <span>{editable ? 'Organisation Administrator' : 'View only'}</span>
        </div>
        <div
          aria-label="Organisation sections"
          className="organisation-page-tabs"
          ref={tabListRef}
          role="tablist"
          onKeyDown={handleTabKeyDown}
        >
          <button
            aria-controls="organisation-overview-panel"
            aria-selected={activeTab === 'overview'}
            className={activeTab === 'overview' ? 'active' : undefined}
            data-tab="overview"
            id="organisation-overview-tab"
            role="tab"
            tabIndex={activeTab === 'overview' ? 0 : -1}
            type="button"
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            aria-controls="organisation-profile-panel"
            aria-selected={activeTab === 'profile'}
            className={activeTab === 'profile' ? 'active' : undefined}
            data-tab="profile"
            id="organisation-profile-tab"
            role="tab"
            tabIndex={activeTab === 'profile' ? 0 : -1}
            type="button"
            onClick={() => setActiveTab('profile')}
          >
            Profile and brand
          </button>
          <button
            aria-controls="organisation-locations-panel"
            aria-selected={activeTab === 'locations'}
            className={activeTab === 'locations' ? 'active' : undefined}
            data-tab="locations"
            id="organisation-locations-tab"
            role="tab"
            tabIndex={activeTab === 'locations' ? 0 : -1}
            type="button"
            onClick={() => setActiveTab('locations')}
          >
            Locations
          </button>
          <button
            aria-controls="organisation-teams-panel"
            aria-selected={activeTab === 'teams'}
            className={activeTab === 'teams' ? 'active' : undefined}
            data-tab="teams"
            id="organisation-teams-tab"
            role="tab"
            tabIndex={activeTab === 'teams' ? 0 : -1}
            type="button"
            onClick={() => setActiveTab('teams')}
          >
            Teams
          </button>
          {personnelPanel ? (
            <button
              aria-controls="organisation-personnel-panel"
              aria-selected={activeTab === 'personnel'}
              className={activeTab === 'personnel' ? 'active' : undefined}
              data-tab="personnel"
              id="organisation-personnel-tab"
              role="tab"
              tabIndex={activeTab === 'personnel' ? 0 : -1}
              type="button"
              onClick={() => setActiveTab('personnel')}
            >
              Personnel
            </button>
          ) : null}
          {activityPanel ? (
            <button
              aria-controls="organisation-activity-panel"
              aria-selected={activeTab === 'activity'}
              className={activeTab === 'activity' ? 'active' : undefined}
              data-tab="activity"
              id="organisation-activity-tab"
              role="tab"
              tabIndex={activeTab === 'activity' ? 0 : -1}
              type="button"
              onClick={() => setActiveTab('activity')}
            >
              Activity
            </button>
          ) : null}
        </div>
        {activeTab === 'overview' ? (
          <div
            aria-labelledby="organisation-overview-tab"
            id="organisation-overview-panel"
            role="tabpanel"
          >
            <dl className="organisation-page-summary">
              <div>
                <dt>Organisation name</dt>
                <dd>{organisationDisplayName(tenant.name)}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{tenant.status.replaceAll('_', ' ')}</dd>
              </div>
              <div>
                <dt>Organisation reference</dt>
                <dd>{tenant.slug}</dd>
              </div>
            </dl>
          </div>
        ) : null}
        {activeTab === 'profile' ? (
          <div
            aria-labelledby="organisation-profile-tab"
            id="organisation-profile-panel"
            role="tabpanel"
          >
            <form className="organisation-profile-form" onSubmit={saveProfile}>
              <label>
                Legal name
                <input
                  disabled={!canManageLogo || busy}
                  required
                  value={legalName}
                  onChange={(event) => setLegalName(event.target.value)}
                />
              </label>
              <label>
                Trading name
                <input
                  disabled={!editable || busy}
                  value={tradingName}
                  onChange={(event) => setTradingName(event.target.value)}
                />
              </label>
              <label>
                Primary contact email
                <input
                  disabled={!editable || busy}
                  type="email"
                  value={contactEmail}
                  onChange={(event) => setContactEmail(event.target.value)}
                />
              </label>
              <label>
                Main phone
                <input
                  disabled={!editable || busy}
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                />
              </label>
              <label>
                Website
                <input
                  disabled={!editable || busy}
                  type="url"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                />
              </label>
              <label>
                Brand colour
                <input
                  disabled={!editable || busy}
                  type="color"
                  value={brandColour}
                  onChange={(event) => setBrandColour(event.target.value)}
                />
              </label>
              <label>
                Organisation logo
                <select
                  disabled={!editable || busy}
                  value={logoFileId}
                  onChange={(event) => setLogoFileId(event.target.value)}
                >
                  <option value="">No logo selected</option>
                  {logoChoices
                    .filter(
                      (file) =>
                        file.status === 'available' &&
                        ['image/png', 'image/jpeg', 'image/webp'].includes(file.contentType),
                    )
                    .map((file) => (
                      <option key={file.id} value={file.id}>
                        {file.fileName}
                      </option>
                    ))}
                </select>
              </label>
              {canManageLogo && onUploadLogo ? (
                <label>
                  Upload logo image
                  <input
                    accept="image/png,image/jpeg,image/webp"
                    disabled={busy || uploadingLogo}
                    type="file"
                    onChange={(event) => void uploadLogo(event.target.files?.[0])}
                  />
                  <small>PNG, JPEG or WebP only, up to 5 MB.</small>
                </label>
              ) : null}
              <label className="organisation-profile-description">
                Organisation description
                <textarea
                  disabled={!editable || busy}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </label>
              <label className="organisation-profile-description">
                Organisation physical address
                <textarea
                  disabled={!editable || busy}
                  placeholder="One line per address line"
                  value={addressLines}
                  onChange={(event) => setAddressLines(event.target.value)}
                />
              </label>
              <label>
                Suburb
                <input
                  disabled={!editable || busy}
                  value={suburb}
                  onChange={(event) => setSuburb(event.target.value)}
                />
              </label>
              <label>
                State or territory
                <input
                  disabled={!editable || busy}
                  value={state}
                  onChange={(event) => setState(event.target.value)}
                />
              </label>
              <label>
                Postcode
                <input
                  disabled={!editable || busy}
                  value={postcode}
                  onChange={(event) => setPostcode(event.target.value)}
                />
              </label>
              <label>
                Country
                <input
                  disabled={!editable || busy}
                  value={country}
                  onChange={(event) => setCountry(event.target.value)}
                />
              </label>
              {editable ? (
                <button disabled={busy} type="submit">
                  {busy ? 'Saving…' : 'Save profile'}
                </button>
              ) : null}
            </form>
          </div>
        ) : null}
      </section>
      {activeTab === 'locations' && apiUrl && accessToken && onDirectoryChanged ? (
        <div
          aria-labelledby="organisation-locations-tab"
          id="organisation-locations-panel"
          role="tabpanel"
        >
          <LocationDirectoryPanel
            accessToken={accessToken}
            editable={editable}
            apiUrl={apiUrl}
            platformSupportReason={platformSupportReason}
            tenantId={tenant.id}
            onChanged={onDirectoryChanged}
          />
        </div>
      ) : null}
      {activeTab === 'teams' && apiUrl && accessToken && onDirectoryChanged ? (
        <div aria-labelledby="organisation-teams-tab" id="organisation-teams-panel" role="tabpanel">
          <TeamDirectoryPanel
            accessToken={accessToken}
            apiUrl={apiUrl}
            editable={editable}
            locations={tenant.locations ?? []}
            managerChoices={managerChoices}
            platformSupportReason={platformSupportReason}
            tenantId={tenant.id}
            onChanged={onDirectoryChanged}
          />
        </div>
      ) : null}
      {activeTab === 'personnel' && personnelPanel ? (
        <div
          aria-labelledby="organisation-personnel-tab"
          id="organisation-personnel-panel"
          role="tabpanel"
        >
          {personnelPanel}
        </div>
      ) : null}
      {activeTab === 'activity' && activityPanel ? (
        <div
          aria-labelledby="organisation-activity-tab"
          id="organisation-activity-panel"
          role="tabpanel"
        >
          {activityPanel}
        </div>
      ) : null}
    </div>
  );
}
